import {
  Button,
  Link,
  Skeleton,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import {
  useGetWorkflowTemplatesQuery,
  WorkflowTypeGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { Typography } from 'packages/common-frontend/components/Typography';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';

export const WorkflowTemplates = ({
  workflowType,
  heading,
  canMultipleTemplatesExist,
}: {
  workflowType: WorkflowTypeGQLType;
  heading: string;
  canMultipleTemplatesExist: boolean;
}) => {
  const {
    user: { organizationId },
  } = useUserAuthOrThrow();

  const workflowTemplateQuery = useGetWorkflowTemplatesQuery({
    fetchPolicy: 'network-only',
    variables: {
      workflowType,
      organizationId,
    },
  });

  if (workflowTemplateQuery.error) {
    Sentry.captureException(workflowTemplateQuery.error);
    throw new Error(
      `Failed to get Workflow Templates for ${workflowType}. Please contact Finley support.`,
    );
  }

  if (!workflowTemplateQuery.data) {
    return <Skeleton height="400px" />;
  }

  const workflowTemplates = workflowTemplateQuery.data.getWorkflowTemplates;

  return (
    <Stack gap="2rem">
      <Stack gap="2em">
        <Typography variant="section-title" trim>
          {heading}
        </Typography>

        {(canMultipleTemplatesExist || workflowTemplates.length === 0) && (
          <Button
            as={RouterLink}
            to="workflow/new"
            colorScheme="v2-aurora"
            variant="secondary"
            width="fit-content"
            leftIcon={<Icon icon="plus" />}
          >
            Add
          </Button>
        )}
      </Stack>

      {workflowTemplates.length > 0 ? (
        <TableContainer>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Name</Th>
              </Tr>
            </Thead>
            <Tbody>
              {workflowTemplates.map((t) => {
                return (
                  <Tr key={t.id}>
                    <Td>
                      <Link
                        as={RouterLink}
                        color={colors.info}
                        to={`/settings/funding-requests/workflow/${t.id}`}
                      >
                        {t.name}
                      </Link>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>
      ) : (
        <Typography>{`No ${heading.toLowerCase()} configured.`}</Typography>
      )}
    </Stack>
  );
};
